import { generateStableActuatorNameFromUuid } from "../../utils/actuatorIds";
import { areBodiesInSameKinematicChain, buildBodyRootMap } from "../../utils/bodyHierarchy";
import { sanitizeMjcfName } from "../../utils/mjcfNames";
import { normalizeMjcfOptionAttributesForMujoco3 } from "../../utils/mjcfOptions";
import { logger } from "../../utils/logger";
import {
  getStartupXmlBuildFlags,
  normalizeStartupPosePolicy,
  type StartupPosePolicy,
} from "../../physics/startupPosePolicy";
import { getBodyTree } from "../sceneActions/bodyUtils";
import { getSceneState } from "../sceneStore";
import { buildMjcfCompilerXml, buildMjcfStatisticXml } from "../mjcfSettingsStore";
import type { FileStoreActions, FileStoreGet, FileStoreSet } from "./types";
import { emitActuatorXml, emitSensorXml } from "./xmlGeneratorActuator";
import { emitEqualityXml, emitTendonXml } from "./xmlGeneratorTendon";
import { buildInitKeyframeQpos, emitKeyframeXml } from "./xmlGeneratorKeyframe";
import { emitContactXml } from "./xmlGeneratorContact";
import { emitAssetXml } from "./xmlGeneratorAssets";
import {
  FALLBACK_DEFAULT_CLASS_NAMES,
  buildMjcfFallbackDefaultXml,
  buildMjcfOptionXml,
  extractDefaultClassNamesFromXml,
  getExportIntegratorName,
  getSceneMjcfCompilerForExport,
  getSceneMjcfStatisticForExport,
  getSceneOptionForExport,
  isScalarJoint,
} from "./xmlGeneratorConfig";
import {
  arrayToString,
  colorToRgba,
  normalizeQuat,
  quatMultiply,
  quatRotateVec,
  reserveUniqueName,
  toMjcfQuatWxyz,
  viewerQuatToMujoco,
  viewerVecToMujoco,
  xmlEscapeAttr,
} from "./xmlGenerationHelpers";
import {
  parseOriginalXmlTendonExportState,
  supplementTendonExportStateFromScene,
} from "./xmlGenerationTendonState";
import { collectBodyNames, collectMissingBodyChildclassDefaults } from "./xmlGeneratorBody";
import type { ExportBodyNode } from "./xmlGenerationTypes";
import { emitBodyTreeXml } from "./xmlGeneratorBodyXml";
import {
  buildAssetUsageAndNames,
  buildBodyExportNames,
  buildCameraExportNames,
  buildCameraValuesForExport,
  buildGeomExportNamesForContactPairs,
  buildJointExportNames,
  buildLightExportNames,
  buildLightValuesForExport,
  buildMaterialExportNames,
  buildMountedCamerasByBodyUuid,
  buildMountedLightsByBodyUuid,
  buildSiteExportNames,
  buildTextureExportNames,
} from "./xmlGenerationExportMaps";
import {
  createFindSiteByIdentifier,
  createHFieldPathResolver,
  createLiveFreeJointBodyPoseResolver,
  createMeshPathResolver,
} from "./xmlGenerationResolvers";
import { buildInitScalarQposByJointUuid } from "./xmlGenerationInitQpos";
import {
  buildJointInfoMaps,
  buildTendonActuatorsByName,
  buildTendonPairs,
  normalizeActuators,
} from "./xmlGenerationTendonPairs";
import { emitWorldbodyXml } from "./xmlGenerationWorldbody";
import {
  DEFAULT_INERTIAL,
  buildDefaultXml,
  buildTendonDefaultClassXml,
} from "./xmlGenerationDefaults";

const MUJOCO_SUPPORTED_MESH_FILE_RE = /\.(obj|stl|msh)(?:[?#].*)?$/i;
const MUJOCO_STL_MAX_FACES = 200_000;

const normalizeMeshPathForSupportCheck = (path: string | null | undefined): string =>
  String(path || "")
    .trim()
    .replace(/\\/g, "/");

const isMjcfSupportedMeshPath = (path: string | null | undefined): boolean => {
  const normalized = normalizeMeshPathForSupportCheck(path);
  return MUJOCO_SUPPORTED_MESH_FILE_RE.test(normalized);
};

const getFileForResolvedPath = (
  fileMap: Record<string, unknown>,
  path: string | null | undefined
): File | null => {
  const normalized = normalizeMeshPathForSupportCheck(path);
  if (!normalized) return null;
  const candidates = [
    normalized,
    normalized.replace(/^\/+/, ""),
    normalized.startsWith("assets/") ? normalized.replace(/^assets\//, "") : `assets/${normalized}`,
  ];
  for (const candidate of candidates) {
    const file = fileMap[candidate];
    if (file instanceof File) return file;
  }
  return null;
};

const isMjcfSupportedStlFile = async (file: File): Promise<boolean> => {
  if (file.size < 84) return false;
  const header = await file.slice(0, 84).arrayBuffer();
  const faceCount = new DataView(header).getUint32(80, true);
  if (!Number.isFinite(faceCount) || faceCount < 1 || faceCount > MUJOCO_STL_MAX_FACES) {
    return false;
  }
  return file.size === 84 + faceCount * 50;
};

const isMjcfSupportedMeshAsset = async ({
  fileMap,
  path,
}: {
  fileMap: Record<string, unknown>;
  path: string | null | undefined;
}): Promise<boolean> => {
  const normalized = String(path || "")
    .trim()
    .replace(/\\/g, "/");
  if (!isMjcfSupportedMeshPath(normalized)) return false;
  if (!/\.stl(?:[?#].*)?$/i.test(normalized)) return true;
  const file = getFileForResolvedPath(fileMap, normalized);
  if (!file) return true;
  return isMjcfSupportedStlFile(file);
};

export const createXmlGenerationActions = ({
  set,
  get,
}: {
  set: FileStoreSet;
  get: FileStoreGet;
}): Pick<FileStoreActions, "generateXmlFromState" | "saveCurrentStateAsXml"> => ({
  generateXmlFromState: async (options?: {
    startupPosePolicy?: StartupPosePolicy;
    xmlNamingMode?: "readable" | "uuid";
    keyframeSource?: "currentScene" | "initKeyframe";
    objectBodyUuids?: string[];
    overrideSceneStateForKeyframe?: Record<string, unknown>;
    skipKeyframeNames?: string[];
  }): Promise<string> => {
    const state = get();
    const sceneState = getSceneState();
    const startupPosePolicy = normalizeStartupPosePolicy(options?.startupPosePolicy);
    const xmlNamingMode = options?.xmlNamingMode ?? "readable";
    const shouldWarnAboutExportOnlyMaterialLoss = xmlNamingMode !== "uuid";
    const keyframeSource = options?.keyframeSource ?? "currentScene";
    const objectBodyUuids = options?.objectBodyUuids ?? [];
    const objectBodyUuidsSet = new Set(objectBodyUuids.filter(Boolean));
    const keyframeDetails = state.keyframeDetails || {};
    const skipKeyframeNames = new Set(
      (options?.skipKeyframeNames || []).map((name) => String(name))
    );
    const preservedMjcfSections = state.preservedMjcfSections || {};
    /** When set (e.g. by warmstart), keyframe is built from this snapshot so no race with IK/store can change the seeded pose. */
    const keyframeSceneState =
      (options?.overrideSceneStateForKeyframe as typeof sceneState) ?? sceneState;
    const startupXmlFlags = getStartupXmlBuildFlags(startupPosePolicy);
    const firstStoredKeyframeQpos = (() => {
      const kf = state.keyframes || {};
      return (Object.values(kf).find((v) => Array.isArray(v) && (v as number[]).length > 0) ??
        null) as number[] | null;
    })();
    const initKeyframeQpos: number[] | null = (() => {
      if (keyframeSource === "initKeyframe" && Array.isArray(state.keyframes?.init)) {
        return state.keyframes.init as number[];
      }
      // verbatimXmlPose: fall back to the first stored keyframe (e.g. "home" for Go2)
      // so the startup hold uses the robot's proper initial pose instead of all-zeros.
      // Go2 and similar robots encode standing pose in a <keyframe>, not in joint ref attrs.
      if (!startupXmlFlags.seedScalarInitQposFromCurrent) {
        return firstStoredKeyframeQpos;
      }
      return null;
    })();
    const sceneOption = getSceneOptionForExport(sceneState);
    const effectiveCompilerSettings = getSceneMjcfCompilerForExport(sceneState);
    let compilerXml = buildMjcfCompilerXml(effectiveCompilerSettings);
    const statisticXml = buildMjcfStatisticXml(getSceneMjcfStatisticForExport(sceneState));
    const bodiesMap = sceneState.bodies || {};
    const worldBodiesList = sceneState.worldBodies || [];
    const bodyRootMap = buildBodyRootMap(bodiesMap, worldBodiesList);

    const jointedBodies = Object.values(bodiesMap).filter(
      (body) => Array.isArray(body.joints) && body.joints.length > 0
    );
    const allJointedBodiesHaveExplicitInertials =
      jointedBodies.length > 0 && jointedBodies.every((body) => body.inertia);
    const [inertiaGroupStart, inertiaGroupEnd] = effectiveCompilerSettings.inertiagrouprange;
    const hasCustomInertiaGroupRange =
      Number.isFinite(Number(inertiaGroupStart)) && Number.isFinite(Number(inertiaGroupEnd));
    if (
      effectiveCompilerSettings.inertiafromgeom === "true" &&
      hasCustomInertiaGroupRange &&
      allJointedBodiesHaveExplicitInertials
    ) {
      logger.warn(
        "[xmlGenerationActions] Exporting with compiler inertiafromgeom=false because a custom inertiagrouprange would override explicit inertials on every jointed body."
      );
      effectiveCompilerSettings.inertiafromgeom = "false";
      compilerXml = buildMjcfCompilerXml(effectiveCompilerSettings);
    }

    // Body UUIDs are used directly as XML names - always unique, maps directly to 3D scene objects

    const {
      tendonCoefficients,
      tendonAttributesByName,
      tendonDefaultAttributesByClass,
      preservedDefaultBlockXml,
    } = await parseOriginalXmlTendonExportState(state.xmlFile);
    const bodyChildclassDefaultNames =
      preservedDefaultBlockXml && preservedDefaultBlockXml.length > 0
        ? extractDefaultClassNamesFromXml(preservedDefaultBlockXml)
        : new Set(FALLBACK_DEFAULT_CLASS_NAMES);
    const geomDefaultClassNames = bodyChildclassDefaultNames;

    const EMIT_ACTUATORS = true;
    const EMIT_INERTIALS = true;

    const {
      joints: jointMap,
      sites: siteMap,
      cameras: cameraMap,
      lights: lightMap,
      materials,
      textures,
      equality,
      contactPairs,
      contactExcludes,
    } = sceneState;
    const {
      spatialTendonDefs,
      spatialReferencedGeomNames,
      tendonUuidToEmittedXmlName,
      tendonRefToEmittedXmlName,
    } = supplementTendonExportStateFromScene({
      sceneTendons: sceneState.tendons,
      joints: jointMap,
      tendonCoefficients,
      tendonAttributesByName,
      tendonDefaultAttributesByClass,
      xmlNamingMode,
    });
    // Direct access to joints map - no tree reconstruction needed
    const joints = Object.values(jointMap);
    const hasBodyConnectEqualities = Object.values(equality || {}).some(
      (eq) => eq.type === "connect" && eq.objtype === "body"
    );

    // Get bodies array for tree traversal
    const bodyTree = getBodyTree(sceneState);
    const bodyTreeForExport = bodyTree as unknown as ExportBodyNode[];
    const bodyChildclassStubNames = new Set<string>();
    collectMissingBodyChildclassDefaults({
      nodes: bodyTreeForExport,
      bodyChildclassDefaultNames,
      bodyChildclassStubNames,
    });

    const findSiteByIdentifier = createFindSiteByIdentifier(siteMap);
    const cameraValuesForExport = buildCameraValuesForExport(cameraMap);
    const mountedCamerasByBodyUuid = buildMountedCamerasByBodyUuid({
      bodyTreeForExport,
      cameraValuesForExport,
    });
    const lightValuesForExport = buildLightValuesForExport(lightMap);
    const mountedLightsByBodyUuid = buildMountedLightsByBodyUuid({
      bodyTreeForExport,
      lightValuesForExport,
    });

    const meshDefinitions = sceneState.meshes || {};
    const hfieldDefinitions = sceneState.hfields || {};
    const { materialUuidToName, materialNameSet } = buildMaterialExportNames(materials);
    const textureUuidToXmlName = buildTextureExportNames(textures);
    const siteExportNameByUuid = buildSiteExportNames({ siteMap, xmlNamingMode });
    const cameraExportNameByUuid = buildCameraExportNames({
      cameraValuesForExport,
      xmlNamingMode,
    });
    const lightExportNameByUuid = buildLightExportNames({
      lightValuesForExport,
      xmlNamingMode,
    });
    const bodyUuidToXmlName = buildBodyExportNames({ bodiesMap, xmlNamingMode });
    const getLiveFreeJointBodyPose = createLiveFreeJointBodyPoseResolver({
      jointMap,
      seedScalarInitQposFromCurrent: startupXmlFlags.seedScalarInitQposFromCurrent,
    });
    const geomUuidToXmlName = buildGeomExportNamesForContactPairs({
      bodiesMap,
      contactPairs,
      xmlNamingMode,
    });
    const jointUuidToXmlName = buildJointExportNames({ jointMap, xmlNamingMode });
    const {
      usedMeshUuids,
      usedHFieldNames,
      meshUuidToXmlName,
      hfieldNameToXmlName,
      standardFloorHField,
    } = buildAssetUsageAndNames({
      bodyTreeForExport,
      meshDefinitions,
      hfieldDefinitions,
    });

    const resolveMeshPathByUuid = createMeshPathResolver({
      fileMap: state.fileMap || {},
      meshDefinitions,
    });
    const fileMap = state.fileMap || {};
    const unsupportedMeshUuidsForMjcf = new Set<string>();
    await Promise.all(
      usedMeshUuids.map(async (meshUuid) => {
        const meshPath = resolveMeshPathByUuid(meshUuid);
        if (!meshPath) return;
        const supported = await isMjcfSupportedMeshAsset({ fileMap, path: meshPath });
        if (!supported) unsupportedMeshUuidsForMjcf.add(meshUuid);
      })
    );
    const usedMeshUuidsForMjcf = usedMeshUuids.filter(
      (meshUuid) => !unsupportedMeshUuidsForMjcf.has(meshUuid)
    );
    const resolveHFieldPathByName = createHFieldPathResolver({
      fileMap: state.fileMap || {},
      hfieldDefinitions,
    });

    const emittedJointNames: string[] = [];
    const jointXmlNameMap: Record<string, string> = {};
    const droppedBodyQuatWarnings: string[] = [];
    const exportWarnings: string[] = [];
    const seenExportWarnings = new Set<string>();
    const addExportWarning = (message: string): void => {
      const normalized = String(message || "").trim();
      if (!normalized || seenExportWarnings.has(normalized)) return;
      seenExportWarnings.add(normalized);
      exportWarnings.push(normalized);
    };
    const initScalarQposByJointUuid = buildInitScalarQposByJointUuid({
      bodyTreeForExport,
      firstStoredKeyframeQpos,
      hasBodyConnectEqualities,
      jointMap,
    });
    const emittedActuatorNames = new Set<string>();
    const actuatorExportNameByRef = new Map<string, string>();
    const rememberActuatorExportName = (
      actuator: { uuid?: string; name?: string } | null | undefined,
      exportName: string,
      fallbackName?: string
    ) => {
      const refs = [
        actuator?.uuid,
        actuator?.name,
        fallbackName,
        sanitizeMjcfName(actuator?.name || ""),
        sanitizeMjcfName(fallbackName || ""),
      ];
      refs.forEach((ref) => {
        const key = typeof ref === "string" ? ref.trim() : "";
        if (key && !actuatorExportNameByRef.has(key)) {
          actuatorExportNameByRef.set(key, exportName);
        }
      });
    };
    const getUniqueActuatorName = (
      preferredRaw: string | undefined | null,
      fallbackRaw: string
    ): string => reserveUniqueName(preferredRaw, fallbackRaw, emittedActuatorNames);
    const getPreferredExportActuatorName = (
      actuator: { uuid?: string; name?: string } | null | undefined,
      fallbackName: string
    ): string => {
      const actuatorUuid = typeof actuator?.uuid === "string" ? actuator.uuid.trim() : "";
      if (actuatorUuid) {
        return generateStableActuatorNameFromUuid(actuatorUuid);
      }
      return actuator?.name || fallbackName;
    };

    const cameraSiteXmlHelpers = {
      arrayToString,
      colorToRgba,
      findSiteByIdentifier,
      normalizeQuat,
      quatMultiply,
      quatRotateVec,
      sanitizeMjcfName,
      toMjcfQuatWxyz,
      viewerQuatToMujoco,
      viewerVecToMujoco,
      xmlEscapeAttr,
    };

    const { worldbodyBodyXml } = emitBodyTreeXml({
      bodyTreeForExport,
      bodyUuidToXmlName,
      bodyChildclassDefaultNames,
      addExportWarning,
      emitInertials: EMIT_INERTIALS,
      defaultInertial: DEFAULT_INERTIAL,
      jointMap,
      jointUuidToXmlName,
      jointXmlNameMap,
      emittedJointNames,
      initScalarQposByJointUuid,
      startupXmlFlags,
      geomDefaultClassNames,
      geomUuidToXmlName,
      spatialReferencedGeomNames,
      materialUuidToName,
      materialNameSet,
      meshUuidToXmlName,
      unsupportedMeshUuidsForMjcf,
      hfieldNameToXmlName,
      meshDefinitions,
      siteExportNameByUuid,
      mountedCamerasByBodyUuid,
      cameraExportNameByUuid,
      mountedLightsByBodyUuid,
      lightExportNameByUuid,
      getLiveFreeJointBodyPose,
      findSiteByIdentifier,
      droppedBodyQuatWarnings,
    });
    const assetXml = emitAssetXml({
      materials,
      textures,
      meshDefinitions,
      hfieldDefinitions,
      fileMap: state.fileMap || {},
      usedMeshUuids: usedMeshUuidsForMjcf,
      usedHFieldNames,
      materialUuidToName,
      textureUuidToXmlName,
      meshUuidToXmlName,
      hfieldNameToXmlName,
      shouldWarnAboutExportOnlyMaterialLoss,
      resolveMeshPathByUuid,
      resolveHFieldPathByName,
      sanitizeMjcfName,
      xmlEscapeAttr,
      arrayToString,
    });

    const allBodyNames = collectBodyNames(bodyTreeForExport);
    const worldbodyXml = emitWorldbodyXml({
      bodyTreeForExport,
      cameraExportNameByUuid,
      cameraSiteXmlHelpers,
      cameraValuesForExport,
      lightExportNameByUuid,
      lightValuesForExport,
      hfieldNameToXmlName,
      materialUuidToName,
      siteExportNameByUuid,
      siteMap,
      standardFloorHField,
      bodyUuidToXmlName,
      worldbodyBodyXml,
    });

    const contactXml = emitContactXml({
      bodyTree: bodyTreeForExport,
      contactExcludes,
      contactPairs,
      bodyUuidToXmlName,
      geomUuidToXmlName,
      sanitizeMjcfName,
      xmlEscapeAttr,
      arrayToString,
    });

    const { jointBySanitizedName, jointInfoByUuid } = buildJointInfoMaps({
      bodiesMap,
      bodyRootMap,
      joints,
    });
    const actuatorsArray = normalizeActuators(sceneState.actuators);
    const tendonActuators = actuatorsArray.filter((a) => a && a.tendon);

    const tendonPairs = buildTendonPairs({
      equality: sceneState.equality,
      jointMap,
      jointBySanitizedName,
      tendonCoefficients,
      tendonActuators,
    });
    const tendonActuatorsByName = buildTendonActuatorsByName(actuatorsArray);

    const {
      tendonXml,
      tendonFixedChunks,
      emittedTendonNames,
      shouldDisableDiscardVisual: shouldDisableDiscardVisualForSpatialTendons,
    } = emitTendonXml({
      tendonPairs,
      tendonCoefficients,
      tendonAttributesByName,
      tendonDefaultAttributesByClass,
      spatialTendonDefs,
      spatialReferencedGeomNames,
      jointXmlNameMap,
      jointInfoByUuid,
      jointBySanitizedName,
      siteExportNameByUuid,
      bodiesMap,
      findSiteByIdentifier,
      sanitizeMjcfName,
      xmlEscapeAttr,
      logger,
    });

    if (
      shouldDisableDiscardVisualForSpatialTendons &&
      effectiveCompilerSettings.discardvisual === "true"
    ) {
      logger.warn(
        "[xmlGenerationActions] Spatial tendons reference wrap geoms; exporting with compiler discardvisual=false so MuJoCo does not remove required tendon route geoms."
      );
      effectiveCompilerSettings.discardvisual = "false";
      compilerXml = buildMjcfCompilerXml(effectiveCompilerSettings);
    }

    const tendonDefaultClassXml = buildTendonDefaultClassXml({
      tendonDefaultAttributesByClass,
      tendonFixedChunks,
    });

    // Original actuator names (already defined above for tendon generation)

    const actuatorXml = emitActuatorXml({
      emitActuators: EMIT_ACTUATORS,
      actuatorsArray,
      bodyTreeForExport,
      allBodyNames,
      jointMap,
      siteMap,
      siteExportNameByUuid,
      jointInfoByUuid,
      jointXmlNameMap,
      jointBySanitizedName,
      tendonPairs,
      tendonActuatorsByName,
      tendonUuidToEmittedXmlName,
      tendonRefToEmittedXmlName,
      emittedTendonNames,
      getPreferredExportActuatorName,
      getUniqueActuatorName,
      rememberActuatorExportName,
      isScalarJoint,
      arrayToString,
      sanitizeMjcfName,
      xmlEscapeAttr,
      logger,
    });
    let keyframeXml = "";
    try {
      const keyframeBodies = keyframeSceneState.bodies || {};
      const qposVals = buildInitKeyframeQpos({
        bodyTreeForExport,
        jointMap,
        keyframeBodies,
        objectBodyUuidsSet,
        initKeyframeQpos,
        startupXmlFlags,
        getLiveFreeJointBodyPose,
        isScalarJoint,
        normalizeQuat,
        sanitizeMjcfName,
        toMjcfQuatWxyz,
      });

      keyframeXml = emitKeyframeXml({
        initQpos: qposVals,
        storedKeyframes: state.keyframes || {},
        keyframeDetails,
        skipKeyframeNames,
        reserveUniqueName,
        arrayToString,
        xmlEscapeAttr,
        addExportWarning,
      });
    } catch (error) {
      logger.error("[FileStore] Error generating keyframe:", error);
    }

    const equalityXml = emitEqualityXml({
      equality,
      jointMap,
      bodiesMap,
      siteMap,
      tendonsMap: sceneState.tendons as Record<string, { name?: string }> | undefined,
      bodyUuidToXmlName,
      siteExportNameByUuid,
      jointXmlNameMap,
      tendonUuidToEmittedXmlName,
      emittedTendonNames,
      jointInfoByUuid,
      jointBySanitizedName,
      reserveUniqueName,
      arrayToString,
      sanitizeMjcfName,
      areBodiesInSameKinematicChain,
    });

    const sensorXml = emitSensorXml({
      sensors: Object.values(sceneState.sensors || {}),
      jointMap,
      siteMap,
      bodiesMap,
      jointXmlNameMap,
      jointUuidToXmlName,
      siteExportNameByUuid,
      bodyUuidToXmlName,
      tendonRefToEmittedXmlName,
      emittedTendonNames,
      actuatorExportNameByRef,
      reserveUniqueName,
      sanitizeMjcfName,
      xmlEscapeAttr,
      logger,
    });

    // Ball joints: Euler avoids sparse Jacobian issues on some WASM builds.
    const integratorName = getExportIntegratorName(sceneOption.integrator, joints, "[FileStore]");
    const customXml = preservedMjcfSections.custom ? `  ${preservedMjcfSections.custom}\n\n` : "";
    const extensionXml = preservedMjcfSections.extension
      ? `  ${preservedMjcfSections.extension}\n\n`
      : "";
    const fallbackDefaultXml = buildMjcfFallbackDefaultXml(tendonDefaultClassXml);
    const defaultXml = buildDefaultXml({
      bodyChildclassStubNames,
      fallbackDefaultXml,
      preservedDefaultBlockXml: preservedDefaultBlockXml || "",
    });

    const completeXml = `<?xml version="1.0" encoding="UTF-8"?>
<mujoco model="${sanitizeMjcfName(state.projectName).toLowerCase()}">
  ${compilerXml}
${statisticXml ? `  ${statisticXml}\n` : ""}

  ${buildMjcfOptionXml(sceneOption, integratorName)}

${extensionXml}${customXml}

${defaultXml}

${contactXml}${assetXml}${worldbodyXml}${tendonXml}${equalityXml}${actuatorXml}${sensorXml}${keyframeXml}</mujoco>`;

    if (droppedBodyQuatWarnings.length) {
      logger.warn(
        "[fileStore] Defaulted to identity for body quaternions:",
        droppedBodyQuatWarnings.join(", ")
      );
    }
    set({ exportWarnings });

    return normalizeMjcfOptionAttributesForMujoco3(completeXml);
  },

  saveCurrentStateAsXml: async (filename?: string): Promise<File> => {
    const state = get();
    const completeXml = await get().actions.generateXmlFromState({
      startupPosePolicy: "currentScenePose",
    });

    const fileName = filename || `${state.projectName.replace(/\s+/g, "_").toLowerCase()}.xml`;
    const xmlBlob = new Blob([completeXml], { type: "application/xml" });
    const xmlFile = new File([xmlBlob], fileName, { type: "application/xml" });

    set({
      xmlFile,
      isNewProject: false,
    });

    return xmlFile;
  },
});
